import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Button} from './button';
import {
    Clock,
    LayoutDashboard,
    Users,
    BarChart3,
    LogOut,
    User,
    ShieldUser,
    Menu,
    X
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './dropdown-menu';
import {Avatar, AvatarFallback} from './avatar';
import {User as UserType} from '../models/User';
import {useState} from 'react';
import '../styles/header.css';

interface HeaderProps {
    user: UserType | null;
    onLogout: () => void;
}

export function Header({user, onLogout}: HeaderProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isActive = (path: string) => location.pathname === path;

    const handleLogout = () => {
        onLogout();
        navigate('/login');
    };

    if (!user) return null;

    const initials = `${user.firstName[0]}${user.lastName[0]}`;

    return (
        <header
            className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container flex h-16 items-center justify-between">
                {/* Logo and Mobile Menu Button */}
                <div className="flex items-center gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <Clock className="logo-icon"/>
                        <span className="logo-text font-semibold">EPI'TIME</span>
                    </Link>

                    {/* Mobile Menu Button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
                    </Button>


                    {/* Desktop Navigation */}
                    <nav className="desktop-nav">
                        <Button
                            variant={isActive('/dashboard') ? 'secondary' : 'ghost'}
                            asChild
                            className="gap-2"
                        >
                            <Link to="/dashboard">
                                <LayoutDashboard className="h-4 w-4"/>
                                Dashboard
                            </Link>
                        </Button>
                        {(user.role === 'MANAGER' || user.role === 'ADMIN') && (
                            <>
                                <Button
                                    variant={isActive('/teams') ? 'secondary' : 'ghost'}
                                    asChild
                                    className="gap-2"
                                >
                                    <Link to="/teams">
                                        <Users className="h-4 w-4"/>
                                        Équipes
                                    </Link>
                                </Button>

                                <Button
                                    variant={isActive('/report') ? 'secondary' : 'ghost'}
                                    asChild
                                    className="gap-2"
                                >
                                    <Link to="/report">
                                        <BarChart3 className="h-4 w-4"/>
                                        Rapports
                                    </Link>
                                </Button>
                            </>
                        )}
                        {user.role === 'ADMIN' && (
                            <Button
                                variant={isActive('/admin') ? 'secondary' : 'ghost'}
                                asChild
                                className="gap-2"
                            >
                                <Link to="/admin">
                                    <ShieldUser className="h-4 w-4"/>
                                    Administrates
                                </Link>
                            </Button>
                        )}
                    </nav>
                </div>

                {/* User Dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative avatar-container rounded-full">
                            <Avatar className="avatar-container">
                                <AvatarFallback className="avatar-fallback">{initials}</AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="dropdown-content" align="end" forceMount>
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col gap-1">
                                <p className="text-sm font-medium">{user.firstName} {user.lastName}</p>
                                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem onClick={() => navigate('/profile')}>
                            <User className="mr-2 h-4 w-4"/>
                            Profile
                        </DropdownMenuItem>
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem onClick={handleLogout}>
                            <LogOut className="mr-2 h-4 w-4"/>
                            Disconnect
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <div className="mobile-nav">
                    <Button
                        variant={isActive('/dashboard') ? 'secondary' : 'ghost'}
                        asChild
                        className="gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <Link to="/dashboard">
                            <LayoutDashboard className="h-4 w-4"/>
                            Dashboard
                        </Link>
                    </Button>
                    {(user.role === 'MANAGER' || user.role === 'ADMIN') && (
                        <>
                            <Button
                                variant={isActive('/teams') ? 'secondary' : 'ghost'}
                                asChild
                                className="gap-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <Link to="/teams">
                                    <Users className="h-4 w-4"/>
                                    Équipes
                                </Link>
                            </Button>

                            <Button
                                variant={isActive('/report') ? 'secondary' : 'ghost'}
                                asChild
                                className="gap-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <Link to="/report">
                                    <BarChart3 className="h-4 w-4"/>
                                    Rapports
                                </Link>
                            </Button>
                        </>
                    )}
                    {user.role === 'ADMIN' && (
                        <Button
                            variant={isActive('/admin') ? 'secondary' : 'ghost'}
                            asChild
                            className="gap-2"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <Link to="/admin">
                                <ShieldUser className="h-4 w-4"/>
                                Administrates
                            </Link>
                        </Button>
                    )}
                </div>
            )}
        </header>
    );
}